import { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { ClipLoader } from "react-spinners";
import { UseFormRegister } from "react-hook-form";
import { DocumentAdd } from "@carbon/icons-react";
import { NewDeliveryFormValues } from "./newDeliveryValidationSchema";

interface DeliveryFileFieldProps {
  register: UseFormRegister<NewDeliveryFormValues>;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  fileName: string | null;
  fileError: string | null;
  isUploading: boolean;
}

/**
 * The purchase-order file attach control for the create-delivery form. The **native labelled file
 * input is kept deliberately** (same pattern sub-flow A used) so the e2e file interaction and the
 * existing `useFileInputUploader` upload contract stay stable — Carbon ships no drop-zone this
 * form's flow needs, and swapping the input type would change how a test drives it. Only the
 * Tailwind styling is gone: the dashed frame and labels now use Carbon tokens via a scoped class,
 * with literal fallbacks per the repo's `var(--cds-*, #literal)` rule.
 */
const DeliveryFileField = ({
  register,
  onChange,
  fileName,
  fileError,
  isUploading,
}: DeliveryFileFieldProps) => {
  const { t } = useTranslation();

  return (
    <div className="new-delivery-form__file">
      <div className="new-delivery-form__dropzone">
        <DocumentAdd size={16} className="new-delivery-form__file-icon" aria-hidden />
        {fileName && (
          <span className="new-delivery-form__file-name">{fileName}</span>
        )}
        <label htmlFor="purchaseOrderFile" className="new-delivery-form__file-cta">
          {isUploading ? (
            <span className="new-delivery-form__file-spinner">
              <ClipLoader loading size={24} />
            </span>
          ) : (
            <span>{t("deliveries.form.upload")}</span>
          )}
          <input
            {...register("purchaseOrderFile")}
            id="purchaseOrderFile"
            name="purchaseOrderFile"
            type="file"
            className="new-delivery-form__file-input"
            onChange={onChange}
          />
        </label>
        <p className="new-delivery-form__file-hint">PDF</p>
      </div>
      {fileError && (
        <span className="new-delivery-form__error">{fileError}</span>
      )}
    </div>
  );
};

export default DeliveryFileField;
